import { Component, Input, ViewContainerRef, ComponentRef, ViewChild, ViewChildren, QueryList, OnInit, OnDestroy, signal, computed, effect, Injector, inject, afterNextRender, Type, untracked, } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, take } from 'rxjs';

import { CardComponent, CardBodyComponent } from '@abp/ng.theme.shared';
import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';

import { ZnTabsComponent } from '../zn-tabs/zn-tabs/zn-tabs';
import { ZnTabComponent } from '../zn-tabs/zn-tab/zn-tab';
import { ZnHierarchyNavigator } from '../zn-hierarchy-navigator/zn-hierarchy-navigator';
import { CrudPage } from '../crud-page/crud-page.component';
import { GenericTable } from '../generic-table/generic-table';
import { EntityConfig, ChildConfig } from '../base-hierarchical-crud/models/hierarchy-config';
import { FieldDefinition } from '../fields/field-definition';
import { FieldMapperHelper } from '../fields/field-mapper.helper';
import { LocalizationService } from '@abp/ng.core';
import { DataTableHelperService } from '../services/data-table-helper';
import { TechnicalNamesReportModalComponent } from '../technical-names-report-modal/technical-names-report-modal';
import { GridStateApplierService } from '../services/grid-state-applier.service';
import { GridSnapshot } from '../layout-list/interface/IGridSnapshot';
import { CurrentPagination } from 'angular-slickgrid';
import { IActionsSchema } from '../zn-other-actions/interfaces/IAction';

interface StackLevel {
  entityName: string;
  selectedRow: any;
  config: EntityConfig;
  componentInstance: any;
  parentFilterField?: string;
  dataset: any[];
  loading: boolean;
  fields: FieldDefinition[];
  gridSnapshot?: GridSnapshot;
  pagination?: CurrentPagination | null;
  selectedChildEntity?: string; // Remember which child tab was selected at this level
}

@Component({
  selector: 'zn-hierarchical-view',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    CardBodyComponent,
    ZnTabsComponent,
    ZnTabComponent,
    ZnHierarchyNavigator,
    CrudPage,
    GenericTable,
    ZendraLocalizationPipe,
    TechnicalNamesReportModalComponent,
  ],
  providers: [DataTableHelperService],
  templateUrl: './zn-hierarchical-view.component.html',
  styleUrls: ['./zn-hierarchical-view.component.scss'],
})
export class ZnHierarchicalViewComponent implements OnInit, OnDestroy {
  @Input() rootComponentInstance!: any;
  @Input() hideCreateButton: boolean = false;
  @Input() disableEdit: boolean = false;
  @Input() disableEditInline: boolean = false;
  @Input() disableCanDelete: boolean = false;
  @Input() disableDuplicate: boolean = false;
  @Input() actions: IActionsSchema | null = null;
  @ViewChild('childComponentContainer', { read: ViewContainerRef })
  childComponentContainer?: ViewContainerRef;
  @ViewChild('rootCrudPage', { read: CrudPage })
  rootCrudPage?: CrudPage;
  @ViewChildren(CrudPage)
  allCrudPages?: QueryList<CrudPage>;
  @ViewChildren(GenericTable)
  accordionTables?: QueryList<GenericTable>;

  private injector = inject(Injector);
  private localizationService = inject(LocalizationService);
  private gridStateApplier = inject(GridStateApplierService);

  private subscriptions: Subscription[] = [];

  isTechnicalNamesModalOpen = false;
  technicalNamesModalConfig: { entityName: string; localizationKey: string; fields: FieldDefinition[] } | null = null;

  private navigationStack = signal<StackLevel[]>([]);
  private activeTabEntity = signal<string | null>(null);
  collapsedLevels = signal<Set<number>>(new Set());
  private shouldRenderNewComponent = signal(false);
  accordionLevels = computed(() => this.navigationStack());
  isNavigating = computed(() => this.navigationStack().length > 0);
  private rootSnapshot = signal<GridSnapshot | null>(null);
  private rootPagination = signal<CurrentPagination | null>(null);

  private tabsCache = new Map<string, ChildConfig[]>();
  private rowSelectionHandlers = new Map<number, { dataView: any; handler: () => void }>();
  private accordionSelectionSubscriptions = new Map<number, any>();
  private pendingParentSelection = new Map<number, number>();
  private childSnapshots = new Map<string, { snapshot?: GridSnapshot; pagination?: CurrentPagination | null }>();
  private updateStackLevel(
    levelIndex: number,
    updater: (level: StackLevel) => StackLevel
  ): void {
    this.navigationStack.update(stack => {
      if (levelIndex < 0 || levelIndex >= stack.length) {
        return stack;
      }
      const updated = [...stack];
      updated[levelIndex] = updater(updated[levelIndex]);
      return updated;
    });
  }

  getTabsForLevel(levelIndex: number): ChildConfig[] {
    const stack = this.navigationStack();
    if (stack.length === 0 || levelIndex >= stack.length) return [];

    const level = stack[levelIndex];
    const selectedRow = level.selectedRow;

    const cacheKey = `${levelIndex}-${selectedRow?.id}`;

    if (this.tabsCache.has(cacheKey)) {
      return this.tabsCache.get(cacheKey)!;
    }

    const children = level.config.getChildren
      ? level.config.getChildren(selectedRow, level.config.children || [])
      : level.config.children || [];

    this.tabsCache.set(cacheKey, children);

    return children;
  }

  getActiveTabForLevel(levelIndex: number): string | null {
    const stack = this.navigationStack();
    // Only the last level in the stack shows which child is currently active
    if (stack.length > 0 && levelIndex === stack.length - 1) {
      return this.activeTabEntity();
    }
    return null;
  }

  private clearTabsCache(): void {
    this.tabsCache.clear();
  }

  private activeTableEntityComputed = computed(() => {
    const active = this.activeTabEntity();

    if (active) {
      return active;
    }

    const stack = untracked(() => this.navigationStack());

    let tabs: ChildConfig[];

    if (stack.length === 0) {
      tabs = this.getRootConfig()?.children || [];
    } else {
      const lastLevel = stack[stack.length - 1];
      tabs = lastLevel.config.getChildren
        ? lastLevel.config.getChildren(lastLevel.selectedRow, lastLevel.config.children || [])
        : lastLevel.config.children || [];
    }

    if (tabs.length > 0) {
      return tabs[0].entityName;
    }

    return null;
  });

  private activeChildConfig = computed(() => {
    const entityName = this.activeTableEntityComputed();

    const stack = untracked(() => this.navigationStack());

    let tabs: ChildConfig[];
    if (stack.length === 0) {
      tabs = this.getRootConfig()?.children || [];
    } else {
      const lastLevel = stack[stack.length - 1];
      tabs = lastLevel.config.getChildren
        ? lastLevel.config.getChildren(lastLevel.selectedRow, lastLevel.config.children || [])
        : lastLevel.config.children || [];
    }

    return tabs.find((c) => c.entityName === entityName);
  });

  private activeChildComponentRef = signal<ComponentRef<any> | null>(null);

  ngOnInit(): void {
    if (this.rootComponentInstance?.drillDownRequested) {
      const sub = this.rootComponentInstance.drillDownRequested.subscribe(
        (event: { row: any; childEntityName: string }) => {
          this.onDrillDownRequested(event);
        }
      );
      this.subscriptions.push(sub);
    }

    afterNextRender(() => {
      this.injectRootCrudPageReference();
    }, { injector: this.injector });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.accordionSelectionSubscriptions.forEach(sub => {
      if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    });
    this.accordionSelectionSubscriptions.clear();
    this.childSnapshots.clear();
    this.clearActiveChildComponent();
  }

  constructor() {
    effect(() => {
      if (this.shouldRenderNewComponent()) {
        const childConfig = this.activeChildConfig();
        if (childConfig) {
          this.shouldRenderNewComponent.set(false);

          afterNextRender(
            () => {
              this.renderActiveChildComponent(childConfig);
            },
            { injector: this.injector }
          );
        }
      }
    });

    effect(() => {
      const navigating = this.isNavigating();

      if (!navigating) {
        afterNextRender(() => {
          this.injectRootCrudPageReference();
        }, { injector: this.injector });
      }
    });
  }

  onDrillDownRequested(event: { row: any; childEntityName: string }): void {
    this.drillDown(event.row, event.childEntityName);
  }

  getRootConfig(): EntityConfig {
    return this.rootComponentInstance?.config;
  }

  getRootFields(): FieldDefinition[] {
    return this.rootComponentInstance?.fieldsWithChevron() || [];
  }

  getRootDataset(): any[] {
    return this.rootComponentInstance?.dataset() || [];
  }

  getRootLoading(): boolean {
    return this.rootComponentInstance?.loading() || false;
  }

  rootLoadData = computed(() => {
    const loadData = this.rootComponentInstance?.loadData;
    return loadData;
  });

  getRootLocalizationKey(): string {
    return this.rootComponentInstance?.config?.localizationKey || '';
  }

  getLevelTitle(level: StackLevel): string {
    return level.config.localizationKey || level.entityName;
  }

  getAccordionColumns(level: StackLevel): any[] {
    const fieldsWithoutChevron = level.fields.filter(f => f.key !== '_chevron');

    return FieldMapperHelper.buildColumnsFromFields(
      fieldsWithoutChevron,
      this.localizationService,
      this.injector
    );
  }

  getAccordionDataset(level: StackLevel): any[] {
    return level.componentInstance?.dataset?.() || [];
  }

  getAccordionLoading(level: StackLevel): boolean {
    return level.componentInstance?.loading?.() || false;
  }

  drillDown(selectedRow: any, childEntityName: string): void {
    const stack = this.navigationStack();

    let parentConfig: EntityConfig;
    let parentComponentInstance: any;
    let parentDataset: any[];
    let parentLoading: boolean;
    let parentFields: FieldDefinition[];
    let parentEntityName: string;

    if (stack.length === 0) {
      parentConfig = this.getRootConfig();
      parentComponentInstance = this.rootComponentInstance;
      parentDataset = this.getRootDataset();
      parentLoading = this.getRootLoading();
      parentFields = this.getRootFields();
      parentEntityName = this.rootComponentInstance.config.entityName;
      const rootState = this.captureGridState(this.rootCrudPage);
      this.rootSnapshot.set(rootState.snapshot || null);
      this.rootPagination.set(rootState.pagination || null);

    } else {
      const activeChildRef = this.activeChildComponentRef();
      if (!activeChildRef) {
        return;
      }

      const childInstance = activeChildRef.instance;
      parentConfig = childInstance.config;
      parentComponentInstance = childInstance;
      parentDataset = childInstance.dataset();
      parentLoading = childInstance.loading();
      parentFields = childInstance.fieldsWithChevron();
      parentEntityName = childInstance.config.entityName;
    }

    const children = parentConfig.getChildren
      ? parentConfig.getChildren(selectedRow, parentConfig.children || [])
      : parentConfig.children || [];

    const childConfig = children.find((c) => c.entityName === childEntityName);

    if (!childConfig) {
      return;
    }

    const parentState = this.captureGridState(parentComponentInstance?.crudPage);

    const newLevel: StackLevel = {
      entityName: parentEntityName,
      selectedRow: selectedRow,
      config: parentConfig,
      componentInstance: parentComponentInstance,
      parentFilterField: parentComponentInstance?.parentFilterField,
      dataset: parentDataset,
      loading: parentLoading,
      fields: parentFields,
      gridSnapshot: parentState.snapshot,
      pagination: parentState.pagination,
      selectedChildEntity: childEntityName, // Remember which child was selected
    };

    this.navigationStack.update((stack) => [...stack, newLevel]);

    this.clearTabsCache();

    const newStackLength = untracked(() => this.navigationStack().length);
    const allLevels = new Set(Array.from({ length: newStackLength }, (_, i) => i));
    this.collapsedLevels.set(allLevels);
    this.activeTabEntity.set(childEntityName);
    this.shouldRenderNewComponent.set(true);
  }

  private resizeAccordionGrid(levelIndex: number): void {
    if (!this.accordionTables) {
      return;
    }

    const tables = this.accordionTables.toArray();
    if (levelIndex >= tables.length) {
      return;
    }

    const table = tables[levelIndex];
    const grid = table.grid?.slickGrid;

    if (!grid) {
      return;
    }

    grid.resizeCanvas();

    const stack = this.navigationStack();
    if (stack[levelIndex] && stack[levelIndex].selectedRow?.id) {
      const rowId = stack[levelIndex].selectedRow.id;
      this.activateAccordionRow(levelIndex, rowId);
    }
  }

  private activateAccordionRow(levelIndex: number, rowId: number): void {
    if (!this.accordionTables) {
      return;
    }

    const tables = this.accordionTables.toArray();

    if (levelIndex >= tables.length) {
      return;
    }

    const table = tables[levelIndex];
    const grid = table.grid?.slickGrid;

    if (!grid) {
      return;
    }

    const dataView = table.grid?.dataView;
    if (!dataView) {
      return;
    }

    const items = dataView.getItems();
    const rowIndex = items.findIndex((item: any) => item.id === rowId);

    if (rowIndex === -1) {
      this.waitForRowAndSelect(levelIndex, rowId, dataView);
      return;
    }

    const columns = grid.getColumns();
    const cellToActivate = columns.length > 1 ? 1 : 0;

    grid.setActiveCell(rowIndex, cellToActivate);
    if (typeof grid.setSelectedRows === 'function') {
      grid.setSelectedRows([rowIndex]);
    }
  }

  private waitForRowAndSelect(levelIndex: number, rowId: number, dataView: any): void {
    if (this.rowSelectionHandlers.has(levelIndex)) {
      return;
    }

    const handler = () => {
      this.clearRowSelectionHandler(levelIndex);
      this.activateAccordionRow(levelIndex, rowId);
    };

    this.rowSelectionHandlers.set(levelIndex, { dataView, handler });

    dataView.onRowCountChanged?.subscribe(handler);
    dataView.onRowsChanged?.subscribe(handler);
  }

  private clearRowSelectionHandler(levelIndex: number): void {
    const entry = this.rowSelectionHandlers.get(levelIndex);
    if (!entry) {
      return;
    }

    const { dataView, handler } = entry;
    dataView.onRowCountChanged?.unsubscribe(handler);
    dataView.onRowsChanged?.unsubscribe(handler);
    this.rowSelectionHandlers.delete(levelIndex);
  }

  navigateBack(toLevel: number): void {
    this.captureAccordionStatesFromView();

    const stackBefore = this.navigationStack();
    if (toLevel >= 0 && stackBefore.length > toLevel + 1) {
      const childLevel = stackBefore[toLevel + 1];
      const key = this.childSnapshotKey(toLevel, childLevel?.entityName);
      if (key) {
        this.childSnapshots.set(key, {
          snapshot: childLevel?.gridSnapshot,
          pagination: childLevel?.pagination ?? null,
        });
      }
    }

    if (toLevel < 0) {
      const stack = this.navigationStack();
      if (stack.length > 0 && stack[0]?.gridSnapshot) {
        this.rootSnapshot.set(stack[0].gridSnapshot);
        this.rootPagination.set(stack[0].pagination ?? null);
      } else {
        const rootState = this.captureGridState(this.rootCrudPage);
        this.rootSnapshot.set(rootState.snapshot || this.rootSnapshot());
        this.rootPagination.set(rootState.pagination ?? this.rootPagination());
      }
      this.navigationStack.set([]);
      this.activeTabEntity.set(null);
      this.clearActiveChildComponent();
      afterNextRender(() => {
        this.applyRootState();
        this.reapplyAccordionParentFilters();
      }, { injector: this.injector });
    } else {
      this.navigationStack.update((stack) => stack.slice(0, toLevel + 1));

      const newStack = untracked(() => this.navigationStack());
      const topLevel = newStack[newStack.length - 1];

      if (topLevel) {
        const children = topLevel.config.getChildren
          ? topLevel.config.getChildren(topLevel.selectedRow, topLevel.config.children || [])
          : topLevel.config.children || [];

        if (children.length > 0) {
          // Restore the previously selected child, or default to first
          const childToActivate = topLevel.selectedChildEntity || children[0].entityName;
          this.activeTabEntity.set(childToActivate);
          this.shouldRenderNewComponent.set(true);
        } else {
          this.activeTabEntity.set(null);
          this.clearActiveChildComponent();
        }
      }
    }

    this.clearTabsCache();

    afterNextRender(() => {
      this.reapplyAccordionParentFilters();
    }, { injector: this.injector });
  }

  onAccordionToggled(levelIndex: number, isExpanded: boolean): void {
    const collapsed = new Set(this.collapsedLevels());
    if (isExpanded) {
      collapsed.delete(levelIndex);
    } else {
      collapsed.add(levelIndex);
    }
    this.collapsedLevels.set(collapsed);

    if (isExpanded) {
      afterNextRender(() => {
        this.resizeAccordionGrid(levelIndex);
        this.applyAccordionState(levelIndex);
      }, { injector: this.injector });
    }
  }

  onAccordionGridReady(levelIndex: number, gridEvent: any): void {
    const grid = gridEvent?.detail || gridEvent;
    if (!grid) {
      return;
    }

    const existingSelectionSub = this.accordionSelectionSubscriptions.get(levelIndex);
    if (existingSelectionSub && typeof existingSelectionSub.unsubscribe === 'function') {
      existingSelectionSub.unsubscribe();
    }
    this.accordionSelectionSubscriptions.delete(levelIndex);

    const selectionSub = grid.slickGrid?.onSelectedRowsChanged?.subscribe((_e: any, args: any) => {
      const rows = args?.rows || [];
      if (rows.length === 0) {
        return;
      }
      const lastRow = rows[rows.length - 1];
      const item = grid.dataView?.getItem(lastRow);
      if (item && !item.isSkeleton && !item.error) {
        this.onAccordionRowClick(levelIndex, item);
      }
    });

    if (selectionSub) {
      this.accordionSelectionSubscriptions.set(levelIndex, selectionSub);
    }

    const stack = this.navigationStack();
    if (levelIndex >= stack.length) {
      return;
    }

    const level = stack[levelIndex];
    const parentFilterApplied = this.applyAccordionParentFilter(levelIndex, grid);

    if (!level?.gridSnapshot) {
      if (parentFilterApplied) {
        grid.extensionService?.refreshBackendDataset(grid.slickGrid?.getOptions());
      }
      return;
    }

    this.gridStateApplier.applySnapshot(grid, level.gridSnapshot, {
      pagination: level.pagination ?? null,
      triggerQuery: true,
    });

    if (level.selectedRow?.id) {
      this.activateAccordionRow(levelIndex, level.selectedRow.id);
    }
  }

  onAccordionRowClick(levelIndex: number, row: any): void {
    const stack = this.navigationStack();
    if (!row?.id) {
      return;
    }

    if (stack[levelIndex] && stack[levelIndex].selectedRow?.id === row.id) {
      return;
    }

    this.navigationStack.update((stack) => {
      const newStack = [...stack];
      if (newStack[levelIndex]) {
        newStack[levelIndex] = {
          ...newStack[levelIndex],
          selectedRow: row,
        };
      }
      return newStack;
    });

    this.clearTabsCache();

    if (levelIndex < stack.length - 1) {
      this.navigateBack(levelIndex);
      return;
    }

    const resolvedParentId = this.resolveParentId(row, this.activeChildConfig());
    if (resolvedParentId !== null && resolvedParentId !== undefined) {
      this.pendingParentSelection.set(levelIndex, resolvedParentId);
    } else {
      this.pendingParentSelection.set(levelIndex, row.id);
    }
    this.applyChildFilterFromLevel(levelIndex);
  }

  onTabChanged(levelIndex: number, tab: any): void {
    const stack = this.navigationStack();
    const currentActiveTab = this.activeTabEntity();

    if (tab.name === currentActiveTab && stack.length === levelIndex + 1) {
      return;
    }

    if (stack.length > levelIndex + 1) {
      this.navigateBack(levelIndex);
    }

    // Update the selectedChildEntity for this level so we remember the choice
    this.navigationStack.update((stack) => {
      if (levelIndex < stack.length) {
        stack[levelIndex].selectedChildEntity = tab.name;
      }
      return [...stack];
    });

    this.activeTabEntity.set(tab.name);

    this.shouldRenderNewComponent.set(true);
  }

  private renderActiveChildComponent(childConfig: ChildConfig): void {
    if (!this.childComponentContainer) {
      return;
    }

    const stack = this.navigationStack();
    const parentLevel = stack.at(-1);
    if (!parentLevel) {
      return;
    }
    const parentId = this.resolveParentId(parentLevel.selectedRow, childConfig);

    if (parentId === null || parentId === undefined) {
      return;
    }

    this.clearActiveChildComponent();

    const componentRef = this.childComponentContainer.createComponent(
      childConfig.component
    );

    (componentRef.instance as any).parentId = parentId;
    (componentRef.instance as any).parentFilterField = childConfig.foreignKeyField;
    (componentRef.instance as any).isUsedInHierarchy = true;

    if ((componentRef.instance as any).drillDownRequested) {
      const sub = (componentRef.instance as any).drillDownRequested.subscribe(
        (event: { row: any; childEntityName: string }) => {
          this.onDrillDownRequested(event);
        }
      );
      this.subscriptions.push(sub);
    }

    this.activeChildComponentRef.set(componentRef);

    componentRef.changeDetectorRef.detectChanges();

    afterNextRender(() => {
      this.injectCrudPageReference(componentRef);
      const stack = this.navigationStack();
      if (stack.length > 0) {
        const levelIndex = stack.length - 1;
        const hasSnapshot =
          this.childSnapshots.has(this.childSnapshotKey(levelIndex, childConfig.entityName) || '');

        this.applyChildFilterFromLevel(levelIndex, true);

        if (hasSnapshot) {
          this.applyChildSnapshotFromLevel(levelIndex, childConfig.entityName);
        }
      }
    }, { injector: this.injector });
  }

  private applyChildFilterFromLevel(levelIndex: number, skipRefresh: boolean = false): void {
    const stack = this.navigationStack();
    if (levelIndex < 0 || levelIndex >= stack.length) {
      return;
    }

    const parentRow = stack[levelIndex]?.selectedRow;
    const resolvedParentId = this.resolveParentId(parentRow, this.activeChildConfig());
    const parentId = this.pendingParentSelection.get(levelIndex) ?? resolvedParentId;

    if (parentId === null || parentId === undefined) {
      return;
    }

    const childRef = this.activeChildComponentRef();
    const childInstance = childRef?.instance;
    if (!childInstance) {
      return;
    }

    if (typeof childRef.setInput === 'function') {
      childRef.setInput('parentId', parentId);
    } else {
      childInstance.parentId = parentId;
      childRef.changeDetectorRef.detectChanges();
    }

    if (!childInstance.crudPage) {
      return;
    }

    const gridInstance = childInstance.crudPage.gridTable.grid;
    const parentFilterField = childInstance.parentFilterField;
    if (parentFilterField) {
      const backendService = gridInstance?.backendService as any;
      if (backendService?.setForcedFilters) {
        backendService.setForcedFilters({ [parentFilterField]: [parentId] });
        backendService.resetPaginationOptions?.();
      }
      if (!skipRefresh && gridInstance?.paginationService) {
        gridInstance.paginationService.goToPageNumber(1, undefined, false);
        gridInstance.paginationService.refreshPagination(false, false, false);
      }
    }

    if (!skipRefresh && gridInstance?.extensionService) {
      gridInstance.extensionService.refreshBackendDataset(gridInstance.slickGrid?.getOptions());
    }
  }

  private applyChildSnapshotFromLevel(levelIndex: number, childEntityName: string): void {
    const key = this.childSnapshotKey(levelIndex, childEntityName);
    if (!key) {
      return;
    }

    const snapshotEntry = this.childSnapshots.get(key);
    if (!snapshotEntry?.snapshot) {
      return;
    }

    const childRef = this.activeChildComponentRef();
    const childInstance = childRef?.instance;
    const crudPage = childInstance?.crudPage;
    const gridTable = crudPage?.gridTable;
    const existingGrid = gridTable?.grid;

    const applySnapshot = (grid: any) => {
      if (!grid) {
        return;
      }

      this.gridStateApplier.applySnapshot(grid, snapshotEntry.snapshot!, {
        pagination: snapshotEntry.pagination ?? null,
        triggerQuery: true,
      });
    };

    if (existingGrid) {
      applySnapshot(existingGrid);
      return;
    }

    if (gridTable?.initializationCompleted) {
      const initSub = gridTable.initializationCompleted
        .pipe(take(1))
        .subscribe((gridEvent: any) => {
          const grid = gridEvent?.detail || gridEvent;
          applySnapshot(grid);
        });
      this.subscriptions.push(initSub);
    }
  }

  private resolveParentId(row: any, childConfig?: ChildConfig): any {
    if (!row) {
      return undefined;
    }
    const field = childConfig?.parentKeyField || childConfig?.foreignKeyField;
    if (field) {
      const resolved = field.split('.').reduce((current, part) => current?.[part], row);
      if (resolved !== null && resolved !== undefined) {
        return resolved;
      }
    }
    return row?.id;
  }

  private childSnapshotKey(levelIndex: number, childEntityName?: string | null): string | null {
    if (childEntityName === undefined || childEntityName === null) {
      return null;
    }
    return `${levelIndex}:${childEntityName}`;
  }

  private injectRootCrudPageReference(): void {

    if (!this.rootComponentInstance) {
      return;
    }

    if (!this.rootCrudPage) {
      return;
    }

    try {
      this.rootComponentInstance.crudPage = this.rootCrudPage;
    } catch (error) {
      console.error('[HierarchicalView] Error injecting root crudPage reference:', error);
    }
  }

  private injectCrudPageReference(componentRef: ComponentRef<any>): void {
    if (!this.allCrudPages) {
      return;
    }

    try {
      const instance = componentRef.instance;
      const crudPages = this.allCrudPages.toArray();
      const childCrudPage = crudPages[crudPages.length - 1];

      if (childCrudPage) {
        instance.crudPage = childCrudPage;
      }
    } catch (error) {
      console.error('[HierarchicalView] Error injecting child crudPage reference:', error);
    }
  }

  private captureGridState(crudPage?: CrudPage): { snapshot?: GridSnapshot; pagination?: CurrentPagination | null } {
    const grid = crudPage?.gridTable?.grid;
    if (!grid) {
      return {};
    }

    return {
      snapshot: this.gridStateApplier.captureSnapshot(grid),
      pagination: grid.backendService?.getCurrentPagination?.() || null,
    };
  }

  private applyRootState(): void {
    const snapshot = this.rootSnapshot();
    if (!snapshot || !this.rootCrudPage?.gridTable?.grid) {
      return;
    }

    this.gridStateApplier.applySnapshot(this.rootCrudPage.gridTable.grid, snapshot, {
      pagination: this.rootPagination(),
      triggerQuery: true,
    });
  }

  private applyAccordionState(levelIndex: number): void {
    if (!this.accordionTables) {
      return;
    }

    const stack = this.navigationStack();
    if (levelIndex >= stack.length) {
      return;
    }

    const level = stack[levelIndex];

    if (!level?.gridSnapshot) {
      return;
    }

    const tables = this.accordionTables.toArray();
    if (levelIndex >= tables.length) {
      return;
    }

    const table = tables[levelIndex];
    const grid = table?.grid;
    if (!grid) {
      return;
    }

    this.applyAccordionParentFilter(levelIndex, grid);

    this.gridStateApplier.applySnapshot(grid, level.gridSnapshot, {
      pagination: level.pagination ?? null,
      triggerQuery: true,
    });
  }

  private applyAccordionParentFilter(levelIndex: number, grid: any): boolean {
    if (!grid) {
      return false;
    }

    if (levelIndex <= 0) {
      return false;
    }

    const stack = this.navigationStack();
    if (levelIndex >= stack.length) {
      return false;
    }

    const parentLevel = stack[levelIndex - 1];
    const level = stack[levelIndex];
    const childConfig = this.findChildConfig(parentLevel, level?.entityName);
    const parentId = this.resolveParentId(parentLevel?.selectedRow, childConfig);

    if (!parentId) {
      return false;
    }

    const parentFilterField = level?.parentFilterField || level?.componentInstance?.parentFilterField || childConfig?.foreignKeyField;
    if (!parentFilterField) {
      return false;
    }

    const backendService =
      (grid as any)?.backendService ||
      (grid as any)?.backendServiceApi?.service ||
      (grid as any)?.gridService?.backendServiceApi?.service ||
      (grid as any)?.options?.backendServiceApi?.service;
    if (backendService?.setForcedFilters) {
      backendService.setForcedFilters({ [parentFilterField]: [parentId] });
      return true;
    }

    return false;
  }

  private findChildConfig(parentLevel: StackLevel | undefined, entityName: string | undefined): ChildConfig | undefined {
    if (!parentLevel || !entityName) {
      return undefined;
    }

    const children = parentLevel.config.getChildren
      ? parentLevel.config.getChildren(parentLevel.selectedRow, parentLevel.config.children || [])
      : parentLevel.config.children || [];

    return children.find(child => child.entityName === entityName);
  }

  private reapplyAccordionParentFilters(): void {
    if (!this.accordionTables) {
      return;
    }

    const tables = this.accordionTables.toArray();
    const stack = this.navigationStack();
    const maxIndex = Math.min(tables.length, stack.length);

    for (let i = 0; i < maxIndex; i += 1) {
      const grid = tables[i]?.grid;
      if (!grid) {
        continue;
      }

      const applied = this.applyAccordionParentFilter(i, grid);
      if (applied) {
        grid.extensionService?.refreshBackendDataset(grid.slickGrid?.getOptions());
      }
    }
  }

  private captureAccordionStatesFromView(): void {
    if (!this.accordionTables) {
      return;
    }

    const tables = this.accordionTables.toArray();
    const stack = this.navigationStack();
    const maxIndex = Math.min(tables.length, stack.length);

    for (let i = 0; i < maxIndex; i += 1) {
      const grid = tables[i]?.grid;
      if (!grid) {
        continue;
      }

      const snapshot = this.gridStateApplier.captureSnapshot(grid);
      const pagination = grid.backendService?.getCurrentPagination?.() ?? null;

      this.updateStackLevel(i, level => ({
        ...level,
        gridSnapshot: snapshot,
        pagination,
      }));
    }
  }

  private clearActiveChildComponent(): void {
    const currentRef = this.activeChildComponentRef();
    if (currentRef) {
      currentRef.destroy();
      this.activeChildComponentRef.set(null);
    }

    if (this.childComponentContainer) {
      this.childComponentContainer.clear();
    }
  }

  onRootCreate(): void {
    this.rootComponentInstance?.create();
  }

  onRootDelete(): void {
    this.rootComponentInstance?.deleteRecords();
  }

  onRootEdit(row: any): void {
    this.rootComponentInstance?.edit(row);
  }

  onRootSaveInMemory(event: any): void {
    this.rootComponentInstance?.onSaveInMemory(event);
  }

  onRootRowSelection(ids: number[]): void {
    this.rootComponentInstance?.onRowSelection(ids);
  }

  onRootExportToExcel(): void {
    this.rootComponentInstance?.exportToExcel();
  }

  onAccordionExportToExcel(levelIndex: number): void {
    const stack = this.navigationStack();
    if (levelIndex >= stack.length) return;

    const level = stack[levelIndex];
    level.componentInstance?.exportToExcel?.();
  }

  onAccordionTechnicalNamesReport(levelIndex: number): void {
    const stack = this.navigationStack();
    if (levelIndex >= stack.length) return;

    const level = stack[levelIndex];
    const fieldsWithoutChevron = level.fields.filter(f => f.key !== '_chevron');

    this.technicalNamesModalConfig = {
      entityName: this.extractEntityName(level.config.localizationKey),
      localizationKey: level.config.localizationKey,
      fields: fieldsWithoutChevron,
    };
    this.isTechnicalNamesModalOpen = true;
  }

  private extractEntityName(localizationKey: string): string {
    const parts = localizationKey.split('.');
    return parts[parts.length - 1] || localizationKey;
  }
}
